import { createSignal, createEffect, For, Show, onMount } from 'solid-js';
import { createStore, reconcile } from 'solid-js/store';
import { useVisibilityPoll } from '../hooks/useVisibilityPoll';
import { useNavigate } from '@solidjs/router';
import { useApi, isOk } from '../api';
import Text from '../components/Text';
import BotStatusBadge, { type BotState } from '../components/BotStatusBadge';
import { useTranslation } from '../hooks/useTranslation';
import { useActiveBot } from '../state/activeBot';
import styles from './PluginOverview.module.css';

const POLL_MS = 10_000;

interface PluginStatusCardDto {
    pluginName: string;
    headlineKey: string;
    headlineArgs?: string[] | null;
    sublineKey?: string | null;
    sublineArgs?: string[] | null;
    status: string;
}

interface PluginInfo {
    name: string;
    isUserFacing: boolean;
    customPagePath?: string | null;
    hasSettings: boolean;
    enabled: boolean;
    statusCard?: PluginStatusCardDto | null;
}

interface BotOverview {
    botId: string;
    minecraftUuid: string;
    minecraftUsername: string;
    isOwner: boolean;
    botRunning: boolean;
    plugins: PluginInfo[];
}

export default function PluginOverview() {
    const api = useApi();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { canViewAll, setBot, bots: contextBots } = useActiveBot();

    const [bots, setBots] = createStore<BotOverview[]>([]);
    const [loaded, setLoaded] = createSignal(false);

    const stateById = () => {
        const map: Record<string, BotState | undefined> = {};
        for (const b of contextBots()) map[b.id] = b.state;
        return map;
    };

    const fetchAll = async () => {
        const res = canViewAll()
            ? await api.botPluginOverview.getOverview({ all: true })
            : await api.botPluginOverview.getOverview();
        if (isOk(res.data)) {
            setBots(reconcile((res.data.value.bots ?? []) as unknown as BotOverview[], { key: 'botId' }));
        }
        setLoaded(true);
    };

    createEffect(() => {
        canViewAll();
        void fetchAll();
    });

    onMount(() => {
        useVisibilityPoll(() => void fetchAll(), POLL_MS);
    });

    const formatStatusText = (key: string | null | undefined, args: string[] | undefined | null) => {
        if (!key) return '';
        return args && args.length > 0 ? t(key, ...args) : t(key);
    };

    const openPlugin = (b: BotOverview, p: PluginInfo) => {
        setBot(b.botId);
        if (p.customPagePath) navigate(p.customPagePath);
        else navigate('/botplugins');
    };

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <h1><Text val="PLUGIN_OVERVIEW_TITLE" /></h1>
                <span class={styles.entryCount}>{bots.length} <Text val="PLUGIN_OVERVIEW_BOTS" /></span>
            </div>
            <p class={styles.help}><Text val="PLUGIN_OVERVIEW_HELP" /></p>

            <Show when={loaded()} fallback={
                <div class={styles.list}>
                    <For each={[0, 1]}>{() =>
                        <div class={`fcard ${styles.skeleton}`}>
                            <div class={styles.skeletonLine} style={{ width: '40%' }} />
                            <div class={styles.skeletonLine} style={{ width: '70%' }} />
                            <div class={styles.skeletonLine} style={{ width: '55%' }} />
                        </div>
                    }</For>
                </div>
            }>
                <Show when={bots.length > 0} fallback={
                    <div class={styles.empty}>
                        <i class="fa-solid fa-puzzle-piece" />
                        <p><Text val="PLUGIN_OVERVIEW_EMPTY" /></p>
                    </div>
                }>
                    <div class={styles.list}>
                        <For each={bots}>{(b) => (
                            <div class={`fcard v ${styles.botCard}`}>
                                <div class={styles.botHeader}>
                                    <img
                                        class={styles.botAvatar}
                                        src={`https://mc-heads.net/avatar/${b.minecraftUuid}/32`}
                                        alt=""
                                    />
                                    <span class={styles.botName}>{b.minecraftUsername}</span>
                                    <BotStatusBadge state={stateById()[b.botId]} />
                                </div>

                                <Show when={b.plugins.length > 0} fallback={
                                    <div class={styles.botEmpty}><Text val="PLUGIN_OVERVIEW_NO_PLUGINS" /></div>
                                }>
                                    <div class={styles.pluginGrid}>
                                        <For each={b.plugins}>{(p) => (
                                            <button
                                                class={styles.pluginCard}
                                                data-status={p.statusCard?.status ?? 'idle'}
                                                classList={{ [styles.pluginCardDisabled]: !p.enabled }}
                                                onClick={() => openPlugin(b, p)}
                                                title={p.enabled ? p.name : t('BOT_PLUGINS_DISABLED')}
                                            >
                                                <div class={styles.pluginHead}>
                                                    <span class={styles.pluginName}>{p.name}</span>
                                                    <Show when={!p.enabled}>
                                                        <span class={styles.disabledTag}><Text val="BOT_PLUGINS_DISABLED" /></span>
                                                    </Show>
                                                </div>
                                                <Show when={p.statusCard} fallback={
                                                    <div class={styles.pluginIdle}>
                                                        <Text val={b.botRunning ? 'PLUGIN_OVERVIEW_NO_DATA' : 'PLUGIN_OVERVIEW_BOT_OFFLINE'} />
                                                    </div>
                                                }>
                                                    <div class={styles.pluginHeadline}>
                                                        {formatStatusText(p.statusCard!.headlineKey, p.statusCard!.headlineArgs)}
                                                    </div>
                                                    <Show when={p.statusCard!.sublineKey}>
                                                        <div class={styles.pluginSubline}>
                                                            {formatStatusText(p.statusCard!.sublineKey, p.statusCard!.sublineArgs)}
                                                        </div>
                                                    </Show>
                                                </Show>
                                            </button>
                                        )}</For>
                                    </div>
                                </Show>
                            </div>
                        )}</For>
                    </div>
                </Show>
            </Show>
        </div>
    );
}
